import { Settings } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useUsersList } from '../../hooks/useUsersList';
import { trpc } from '../../lib/trpc';
import { Button } from '../../ui/button';
import { UsersTable } from './UsersTable';

/**
 * The workspace member directory, rendered as `default` by both portals' `users`
 * route. Read-only: it answers "who is in this workspace and how do I reach them",
 * and hands off to Settings for the admin-gated work of inviting, changing roles,
 * or removing people.
 */
export function UsersPage() {
  const { workspaceId, orgId } = useParams<{ workspaceId: string; orgId: string }>();
  const usersList = useUsersList();

  const meQuery = trpc.auth.me.useQuery();
  const currentUserQuery = trpc.users.me.useQuery();
  const activeWorkspace = meQuery.data?.workspaces.find((w) => w.id.toString() === workspaceId);
  const isAdmin = activeWorkspace?.role === 'admin';

  return (
    <main className="flex-1 p-6 lg:p-10 space-y-8 bg-slate-50/50 dark:bg-slate-950/50 overflow-y-auto h-full scrollbar-thin">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Users</h1>
          <p className="text-muted-foreground mt-1">
            Everyone with access to this workspace. Each person maintains their own profile.
          </p>
        </div>
        {isAdmin && (
          <Button asChild variant="outline" className="w-full md:w-auto">
            <Link to={`/org/${orgId}/workspace/${workspaceId}/settings`}>
              <Settings className="mr-2 h-4 w-4" />
              Manage Members
            </Link>
          </Button>
        )}
      </div>

      <UsersTable
        items={usersList.items}
        isLoading={usersList.isLoading}
        sort={usersList.sort}
        onToggleSort={usersList.toggleSort}
        currentUserId={currentUserQuery.data?.id}
        pageStart={usersList.pageStart}
        pageEnd={usersList.pageEnd}
        totalCount={usersList.totalCount}
        hasNextPage={usersList.hasNextPage}
        hasPrevPage={usersList.hasPrevPage}
        onNextPage={usersList.goToNextPage}
        onPrevPage={usersList.goToPrevPage}
      />
    </main>
  );
}
